// =============================================================================
// src/features/analytics/analytics-insights-markup.ts - Render da edicao dos
// Cards de Inteligencia Tatica (capa, tabela de prioridade, blocos, rodape).
// Parametrizado por AnalyticsInsightEditionDoc (2026-07-28): o default e o port
// estatico de 25/07; as edicoes vivas chegam parseadas do markdown do banco.
// Contrato de confianca dos campos *Html: no doc estatico sao conteudo
// versionado; nos docs parseados sao gerados EXCLUSIVAMENTE por
// analyticsInsightInlineHtml (escape-first) — por isso entram sem re-escape.
// Rotulos simples passam por escapeHtml aqui.
// =============================================================================

import { escapeHtml } from '../../app/dom';
import {
  analyticsInsightPriorityRowsForBlocks,
  analyticsInsightsStaticEditionDoc,
} from './analytics-insights-data';
import type { AnalyticsInsightGenericSection } from './analytics-insights-edition-parser';
import type {
  AnalyticsInsightBlock,
  AnalyticsInsightCard,
  AnalyticsInsightClassification,
  AnalyticsInsightEdition,
  AnalyticsInsightEditionDoc,
  AnalyticsInsightEvidenceBlock,
  AnalyticsInsightPriorityRow,
  AnalyticsInsightTable,
} from './analytics-insights-types';

/** Variante visual do chip por classificacao (lista fechada da skill).
 * CORREÇÃO DE ROTA (novo em 27/07) e informativo-neutro: azul, nunca ameaca. */
const ANALYTICS_INSIGHT_CLASSIFICATION_CLASS: Record<AnalyticsInsightClassification, string> = {
  'AMEAÇA ESTRUTURAL': 'is-ameaca',
  'AMEAÇA DE PAUTA': 'is-ameaca',
  OPORTUNIDADE: 'is-oportunidade',
  'OPORTUNIDADE PERDIDA': 'is-oportunidade-perdida',
  'TERRENO DE IMPRENSA': 'is-imprensa',
  VIGILÂNCIA: 'is-vigilancia',
  'CORREÇÃO DE ROTA': 'is-correcao',
  'LIMITE DE MEDIÇÃO': 'is-medicao',
};

/** Corpo completo do documento, na ordem da fonte; default = port estatico. */
export function buildAnalyticsInsightsViewMarkup(
  editionDoc: AnalyticsInsightEditionDoc = analyticsInsightsStaticEditionDoc(),
): string {
  const blocksMarkup = editionDoc.blocks.map(analyticsInsightsBlockMarkup).join('');
  return `${analyticsInsightsCoverMarkup(editionDoc.edition)}${analyticsInsightsPriorityTableMarkup(editionDoc.blocks)}${blocksMarkup}
    <footer class="u-analytics-insight-doc-foot"><em>${escapeHtml(editionDoc.edition.documentFooter)}</em></footer>`;
}

function analyticsInsightsCoverMarkup(edition: AnalyticsInsightEdition): string {
  const coverNotesMarkup = edition.coverNotesHtml
    .map((coverNoteHtml) => `<p class="u-analytics-insight-note">${coverNoteHtml}</p>`)
    .join('\n    ');
  return `<header class="u-analytics-insight-cover">
    <div class="u-analytics-insight-brand">ATALAIA · MONITORAMENTO ESTRATÉGICO · CONFIDENCIAL</div>
    <h1 id="analyticsDigestTitle">Cards de Inteligência Tática</h1>
    <p class="u-analytics-insight-monitored">${edition.monitoredHtml}</p>
    ${analyticsInsightsCoverFactsMarkup(edition)}
    ${coverNotesMarkup}
  </header>`;
}

function analyticsInsightsCoverFactsMarkup(edition: AnalyticsInsightEdition): string {
  const coverFacts: readonly (readonly [string, string])[] = [
    ['Corte', edition.cutLabel],
    ['Base analítica', edition.analyticalWindowLabel],
    ['Extração', edition.extractionLabel],
    ['Volumes da base', edition.baseVolumesLabel],
    ['Cards', edition.cardCountLabel],
  ];
  const factsMarkup = coverFacts
    .map(([factLabel, factValue]) => `<div><span>${escapeHtml(factLabel)}</span><strong>${escapeHtml(factValue)}</strong></div>`)
    .join('');
  return `<div class="u-analytics-insight-facts">${factsMarkup}</div>`;
}

function analyticsInsightsPriorityTableMarkup(editionBlocks: readonly AnalyticsInsightBlock[]): string {
  const priorityRowsMarkup = analyticsInsightPriorityRowsForBlocks(editionBlocks)
    .map(analyticsInsightsPriorityRowMarkup)
    .join('');
  return `<section class="u-analytics-panel u-analytics-insight-priority">
    <div class="u-analytics-panel-head"><div><span>Cards em ordem de impacto</span><h2>Prioridade da edição</h2></div><i class="ti ti-list-numbers"></i></div>
    <div class="u-analytics-insight-table-scroll"><table>
      <thead><tr><th>Impacto</th><th>Card</th><th>Classificação</th></tr></thead>
      <tbody>${priorityRowsMarkup}</tbody>
    </table></div>
  </section>`;
}

function analyticsInsightsPriorityRowMarkup(priorityRow: AnalyticsInsightPriorityRow): string {
  const cardLabel = `CARD ${String(priorityRow.cardNumber).padStart(2, '0')} — ${priorityRow.priorityTitle}`;
  return `<tr><td><strong>${priorityRow.impactScore}/5</strong></td><td>${escapeHtml(cardLabel)}</td>
    <td>${analyticsInsightClassificationChipMarkup(priorityRow.classificationLabel)}</td></tr>`;
}

function analyticsInsightsBlockMarkup(insightBlock: AnalyticsInsightBlock): string {
  return `<section class="u-analytics-insight-block" data-analytics-insight-block="${insightBlock.blockId}">
    <h2 class="u-analytics-insight-block-title">${escapeHtml(insightBlock.blockTitle)}</h2>
    ${insightBlock.blockCards.map(analyticsInsightCardMarkup).join('')}
  </section>`;
}

function analyticsInsightCardMarkup(insightCard: AnalyticsInsightCard): string {
  return `<article class="u-analytics-panel u-analytics-insight-card" data-analytics-insight-card="${insightCard.cardNumber}">
    ${analyticsInsightCardHeadMarkup(insightCard)}
    <div class="u-analytics-insight-section"><h4>O que os dados mostram</h4>
      ${insightCard.evidenceBlocks.map(analyticsInsightEvidenceBlockMarkup).join('')}</div>
    <div class="u-analytics-insight-section"><h4>Leitura tática</h4>
      ${insightCard.tacticalReadingHtml.map((readingHtml) => `<p>${readingHtml}</p>`).join('')}</div>
    ${analyticsInsightActionMarkup(insightCard)}
    ${analyticsInsightMetaFooterMarkup(insightCard)}
  </article>`;
}

function analyticsInsightCardHeadMarkup(insightCard: AnalyticsInsightCard): string {
  const cardCode = `CARD ${String(insightCard.cardNumber).padStart(2, '0')}`;
  return `<header class="u-analytics-insight-card-head">
    <div class="u-analytics-insight-card-tags"><span class="u-analytics-insight-impact is-impact-${insightCard.impactScore}">IMPACTO ${insightCard.impactScore}/5</span>
    ${analyticsInsightClassificationChipMarkup(insightCard.classificationLabel)}</div>
    <h3><em>${cardCode}</em> ${escapeHtml(insightCard.cardTitle)}</h3>
  </header>`;
}

function analyticsInsightClassificationChipMarkup(classificationLabel: AnalyticsInsightClassification): string {
  const chipClass = ANALYTICS_INSIGHT_CLASSIFICATION_CLASS[classificationLabel];
  return `<span class="u-analytics-insight-class ${chipClass}">${escapeHtml(classificationLabel)}</span>`;
}

function analyticsInsightEvidenceBlockMarkup(evidenceBlock: AnalyticsInsightEvidenceBlock): string {
  if (evidenceBlock.blockKind === 'table') return analyticsInsightEvidenceTableMarkup(evidenceBlock);
  if (evidenceBlock.blockKind === 'quote') {
    return `<blockquote class="u-analytics-insight-quote"><p>${evidenceBlock.quoteHtml}</p>
      <cite>— ${escapeHtml(evidenceBlock.quoteAttribution)}</cite></blockquote>`;
  }
  return `<p>${evidenceBlock.paragraphHtml}</p>`;
}

function analyticsInsightEvidenceTableMarkup(tableBlock: AnalyticsInsightTable): string {
  const headerMarkup = tableBlock.columnHeaders.map((columnHeader) => `<th>${escapeHtml(columnHeader)}</th>`).join('');
  const rowsMarkup = tableBlock.tableRows
    .map((tableRow) => `<tr>${tableRow.map((tableCell) => `<td>${tableCell}</td>`).join('')}</tr>`)
    .join('');
  return `<div class="u-analytics-insight-table-scroll"><table>
    <thead><tr>${headerMarkup}</tr></thead><tbody>${rowsMarkup}</tbody>
  </table></div>`;
}

function analyticsInsightActionMarkup(insightCard: AnalyticsInsightCard): string {
  return `<div class="u-analytics-insight-action"><h4>Ação</h4><p>${insightCard.actionCallHtml}</p></div>`;
}

function analyticsInsightMetaFooterMarkup(insightCard: AnalyticsInsightCard): string {
  const metaEntries: readonly (readonly [string, string])[] = [
    ['Confiança', insightCard.confidenceLine],
    ['Natureza', insightCard.natureLine],
    ['Base', insightCard.baseLine],
    ['Validade', insightCard.validityLine],
    ['Série', insightCard.seriesLine],
  ];
  const metaMarkup = metaEntries
    .map(([metaLabel, metaValue]) => `<span><b>${escapeHtml(metaLabel)}:</b> ${escapeHtml(metaValue)}</span>`)
    .join(' · ');
  return `<footer class="u-analytics-insight-meta">${metaMarkup}</footer>`;
}

/** Render GENERICO (nota de deriva / documento fora do contrato de cards):
 * titulos + paragrafos/tabelas, honesto ao conteudo do dia. O 1o heading vira
 * o h1 com id="analyticsDigestTitle" (ancora do aria-labelledby da section). */
export function buildAnalyticsInsightGenericDocMarkup(
  genericSections: readonly AnalyticsInsightGenericSection[],
): string {
  const sectionsMarkup = genericSections
    .map((genericSection, sectionIndex) => analyticsInsightGenericSectionMarkup(genericSection, sectionIndex))
    .join('');
  return `<div class="u-analytics-insight-generic">${sectionsMarkup}</div>`;
}

function analyticsInsightGenericSectionMarkup(
  genericSection: AnalyticsInsightGenericSection,
  sectionIndex: number,
): string {
  const headingMarkup =
    sectionIndex === 0
      ? `<h1 id="analyticsDigestTitle">${genericSection.headingHtml ?? 'Resumo de IA'}</h1>`
      : genericSection.headingHtml === null
        ? ''
        : `<h2 class="u-analytics-insight-block-title">${genericSection.headingHtml}</h2>`;
  const bodyMarkup = genericSection.bodyBlocks.map(analyticsInsightEvidenceBlockMarkup).join('');
  return `<section class="u-analytics-insight-generic-section">${headingMarkup}${bodyMarkup}</section>`;
}
